import { Form, Switch, Tooltip, Typography } from "antd";
import {
  FormFieldName,
  FormHeaderTextarea,
  FormSelect,
  FormTextarea,
} from "../../../components/form/form";
import RolePermissions from "./role-permissions";
import { SITE_SCOPING_OPTIONS } from "./roles-filter";
import { DESCRIPTION_MAX, NAME_MAX, toggle } from "./role-edit";

// A field the form refuses is marked the way the task card marks one.
const invalid = (error) => (error ? { validateStatus: "error", help: error } : {});

/** A labelled row, laid out like the fields of the other cards. */
export const Field = ({ label, error, children }) => (
  <Form.Item className="role-card__field" {...invalid(error)}>
    <FormFieldName text={`${label}:`} />
    {children}
  </Form.Item>
);

/** A value nobody edits here, lined up with the editable ones. */
export const Value = ({ children }) => <span className="lf-form-element">{children}</span>;

/** A section heading inside the card: the field label, on its own line. */
export const Section = ({ label, error, children }) => (
  <div className="role-card__section">
    <FormFieldName text={label} />
    {children}
    {error && <Typography.Text type="danger" role="alert">{error}</Typography.Text>}
  </div>
);

/**
 * LF360-2045 / 2046 / 2047 — a role's fields: read-only on its card, editable
 * while editing it or creating one.
 *
 * `errors` are the fields' problems to show, from `fieldErrors`.
 * `scopingLock` is the reason site scoping cannot change, if any.
 * `startFrom` (creating only) is { roles, value, onChange }: picking a role
 * copies its permissions and site scoping into the form.
 * `children` are read-only rows shown under site scoping.
 */
const RoleForm = ({
  catalog,
  draft,
  setDraft,
  readonly = false,
  errors = {},
  scopingLock = null,
  startFrom,
  children,
}) => (
  <>
    <Form.Item style={{ paddingBottom: 24, marginBottom: 0 }} {...invalid(errors.name)}>
      <FormHeaderTextarea
        data-testid="role-name"
        aria-label="Name"
        readonly={readonly}
        placeholder="Enter role name…"
        value={draft.name}
        maxLength={NAME_MAX}
        onChange={(event) => setDraft?.({ ...draft, name: event.target.value })}
      />
    </Form.Item>
    <Field label="Description" error={errors.description}>
      <FormTextarea
        aria-label="Description"
        readonly={readonly}
        placeholder={readonly ? "Description" : "Describe who this role is for…"}
        value={draft.description}
        maxLength={DESCRIPTION_MAX}
        onChange={(event) => setDraft?.({ ...draft, description: event.target.value })}
      />
    </Field>
    {startFrom && (
      <Field label="Start from">
        <div>
          <FormSelect
            aria-label="Start from"
            value={startFrom.value}
            placeholder="Select role to copy…"
            options={[
              { value: "", label: "Blank" },
              ...startFrom.roles.map((role) => ({ value: role.id, label: role.name })),
            ]}
            onChange={(value) => startFrom.onChange(value || null)}
            style={{ minWidth: 220 }}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </div>
      </Field>
    )}
    <Field label="Site scoping">
      <Tooltip title={readonly ? null : scopingLock}>
        <div>
          <FormSelect
            aria-label="Site scoping"
            value={draft.siteScoping}
            readonly={readonly}
            disabled={!readonly && Boolean(scopingLock)}
            options={SITE_SCOPING_OPTIONS.map((o) => ({ value: o.id, label: o.name }))}
            onChange={(value) => setDraft?.({ ...draft, siteScoping: value })}
            style={{ minWidth: 220 }}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </div>
      </Tooltip>
    </Field>
    {children}

    <Section label="Permissions" error={errors.permissions}>
      <RolePermissions
        catalog={catalog}
        permissions={draft.permissions}
        renderAllowed={readonly ? undefined : (permission, held) => (
          <Switch
            data-testid={`role-permission-${permission.key}`}
            size="small"
            aria-label={permission.label}
            checked={held}
            onChange={(on) => setDraft({
              ...draft,
              permissions: toggle(catalog, draft.permissions, permission.key, on),
            })}
          />
        )}
      />
    </Section>
  </>
);

export default RoleForm;
